import { Button, Spin } from 'antd';
import React, { useState } from 'react';

import { useGetBlockEventsName, useGetMappedStatusEvent } from '@api/emp/queryHooks';
import {
  BlockEventName,
  CusSelect,
  InputBox,
  TopBar,
  TopBarLeft,
  Wrapper,
} from '@components/block-event-single/components/BlockEventSingleRuleGroup/styled';
import { IBlockEventSingleRuleGroup } from '@components/block-event-single/components/BlockEventSingleRuleGroup/typed';
import BlockRuleRow from '@components/block-event-single/components/BlockRuleRow';
import {
  BlockRuleTypeOptions,
  EBlockEventSingleChangeType,
  EBlockEventSingleRuleActionType,
  EVENT_NAME_WITH_BLOCK_RULE,
} from '@components/block-event-single/constant/constant';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import { buildOptionsWithTag } from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { Icon } from '@components/metrics-builder/components/MetricList';
import AddRuleIconSvg from '@icons/mapping-panel/add-rule-icon.svg';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { mappedEventDimensionType } from '@src/common/constant/enum';

const BlockEventSingleRuleGroup: React.FC<IBlockEventSingleRuleGroup> = ({
  mode,
  index,
  data,
  blockType,
  onChange,
  onBlockRulesChange,
  onBlockTypeChange,
}) => {
  const { data: mappedEventNameData, isLoading: isMappedEventNameDataLoading } =
    useGetMappedStatusEvent('event_name');
  const { data: blockedEventsName, isLoading: isBlockedEventsNameLoading } =
    useGetBlockEventsName();
  const { data: eventNameData, isFetching: isEventNameLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.all_event_name],
      filters: [],
    },
    500,
  );

  const [searchText, setSearchText] = useState<string | undefined>(data?.event_name);

  const isBlockIf = blockType === BlockRuleTypeOptions[1].value;
  const isOnlyBlockAll =
    typeof data?.event_name === 'undefined' ||
    !EVENT_NAME_WITH_BLOCK_RULE.includes(data?.event_name);
  const isBlockEventNameActive = typeof data?.event_name === 'string' && data?.event_name !== '';

  const allBlockEventsOptions = buildOptionsWithTag(
    eventNameData || [],
    mappedEventDimensionType.all_event_name,
    searchText,
    mappedEventNameData?.data,
    blockedEventsName,
    true,
    true,
    EVENT_NAME_WITH_BLOCK_RULE,
    /^conviva_(?!network_request$)/,
  );

  return (
    <Wrapper active={mode !== EPanelOpenMethod.VIEW}>
      <TopBar>
        <TopBarLeft>
          <InputBox>
            <BlockEventName>Block Event Name *</BlockEventName>
            <span style={{ padding: '0 8px 0 6px' }}>=</span>
            <CusSelect
              allowClear
              showSearch
              showArrow={false}
              disabled={mode !== EPanelOpenMethod.CREATE}
              active={isBlockEventNameActive}
              value={data?.event_name}
              placeholder="Please select..."
              onChange={(value) => {
                setSearchText(value);
                onChange?.(null, null, EBlockEventSingleChangeType.BLOCK_EVENT_SINGLE_NAME, value);
              }}
              onSearch={(value) => {
                setSearchText(value);
              }}
              options={allBlockEventsOptions}
              optionLabelProp="value"
              filterOption={(input, option) =>
                (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownRender={(originNode) => {
                return (
                  <Spin
                    spinning={
                      isMappedEventNameDataLoading ||
                      isBlockedEventsNameLoading ||
                      isEventNameLoading
                    }
                  >
                    {originNode}
                  </Spin>
                );
              }}
              style={{ width: 450 }}
            />
          </InputBox>
        </TopBarLeft>
        {mode === EPanelOpenMethod.CREATE ? (
          <Icon
            style={{ marginRight: '8px' }}
            component={DeleteSvg}
            activeComponent={DeleteActiveSvg}
            onClick={(e) => {
              e.stopPropagation();
              onBlockRulesChange?.(EBlockEventSingleRuleActionType.DELETE_ALL, index);
              onBlockTypeChange?.(BlockRuleTypeOptions[0].value);
            }}
          />
        ) : null}
      </TopBar>
      <div style={{ padding: '8px 12px 8px 68px' }}>
        {data?.block_rules?.length === 0 || isOnlyBlockAll ? (
          <BlockRuleRow
            mode={mode}
            index={0}
            eventName={data?.event_name}
            blockType={blockType}
            onBlockTypeChange={onBlockTypeChange}
          />
        ) : (
          data?.block_rules?.map((subAndBlockRules, subAndIndex) => {
            return subAndBlockRules.map((ruleItem, subOrIndex) => {
              return (
                <BlockRuleRow
                  key={`BlockRuleRow_${subOrIndex}`}
                  mode={mode}
                  index={subOrIndex}
                  eventName={data?.event_name}
                  blockType={blockType}
                  data={ruleItem}
                  onChange={(...rest) => onChange?.(subAndIndex, subOrIndex, ...rest)}
                  onBlockTypeChange={onBlockTypeChange}
                  blockRules={data?.block_rules}
                  onDelete={() => {
                    onBlockRulesChange?.(
                      EBlockEventSingleRuleActionType.DELETE_RULE_ITEM,
                      index,
                      subAndIndex,
                      subOrIndex,
                    );
                  }}
                />
              );
            });
          })
        )}
      </div>
      {isBlockIf && mode !== EPanelOpenMethod.VIEW && (
        <div style={{ padding: '0px 8px 16px 221px' }}>
          <Button
            type="link"
            icon={<Icon component={AddRuleIconSvg} style={{ fontSize: 16 }} />}
            onClick={() => {
              onBlockRulesChange?.(EBlockEventSingleRuleActionType.ADD_OR_RULE_ITEM, index);
            }}
            size="middle"
          >
            Add OR Rule
          </Button>
        </div>
      )}
    </Wrapper>
  );
};

export default BlockEventSingleRuleGroup;
